import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { MenuIcon } from '@/components/MenuIcon';
import { PageHeader } from '@/components/PageHeader';
import { PhotoGrid } from '@/components/PhotoGrid';
import { QueryState } from '@/components/QueryState';
import { InspectionBadge } from '@/components/StatusBadge';
import { shortDate } from '@/lib/format';
import { keys, useDownloadInspectionReport, useInspection, useInspectionAction } from '@/lib/queries';
import { useSite } from '@/lib/useSite';
import type { DefectType, Inspection } from '@/lib/types';

const DEFECT_TYPES: DefectType[] = ['누락', '치수', '오염', '손상', '시공순서', '자재불량', '안전', '기타'];

/**
 * 검측 한 건.
 *
 * <p>상태 전이는 이미 서버에 다 있다(`InspectionService`) — 새로 만들지 않고 그대로 부른다.
 * <pre>
 *   REQUESTED ──착수(판정하는 손)──▶ IN_REVIEW ──판정(판정하는 손)──▶ PASSED
 *                                                          └──────▶ FAILED ──재검 요청(증빙 만드는 손)──▶ 새 회차
 * </pre>
 *
 * <p>판정하는 손은 감리와 직원, 증빙을 만드는 손은 협력사와 직원이다(DOC-87 §3-3). 직원은 둘 다 한다 —
 * 시공사 자체 검측이 그렇다. 감리만 「판정하는 손이 증빙을 만들지 않는다」 선에 남는다(§3-4).
 *
 * <p><b>재검은 앞 행을 덮지 않는다.</b> 새 행이 생기고 회차가 오른다 — 그래서 몇 번을 물렸는지가
 * 기록으로 남는다.
 */
export function InspectionDetailPage() {
  const { inspectionId = '' } = useParams();
  const { siteId, isJudge, canSubmitEvidence } = useSite();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const inspection = useInspection(inspectionId);
  const { start, judge, recheck } = useInspectionAction(siteId, inspectionId);
  const report = useDownloadInspectionReport();

  const [verdictOpen, setVerdictOpen] = useState(false);
  const [resultNote, setResultNote] = useState('');
  const [defectType, setDefectType] = useState<DefectType>('치수');

  const row = inspection.data;

  async function onJudge(status: 'PASSED' | 'FAILED') {
    // 불합격은 지적사항이 필수다 — 서버도 `INSPECTION_NOTE_REQUIRED` 로 막는다.
    if (status === 'FAILED' && resultNote.trim() === '') return;
    await judge.mutateAsync({
      status,
      resultNote: resultNote.trim(),
      defectType: status === 'FAILED' ? defectType : null,
    });
    setVerdictOpen(false);
    setResultNote('');
  }

  async function onRecheck() {
    const created = await recheck.mutateAsync();
    navigate(`/sites/${siteId}/inspections/${created.id}`, { replace: true });
  }

  return (
    <QueryState
      pending={inspection.isPending}
      error={inspection.isError}
      empty={row === null || row === undefined}
      emptyText="검측을 찾을 수 없습니다."
    >
      {row !== null && row !== undefined && (
        <>
          <PageHeader
            title={row.title}
            subtitle={`${row.inspectionType} · ${row.trade ?? '공종 미지정'}${row.roundNo > 1 ? ` · ${row.roundNo}차` : ''}`}
            action={
              <div className="flex items-center gap-2">
                <InspectionBadge status={row.status} />
                {/*
                  보고서 — 회차별 사진·지적·판정 한 장(PDF). 세 당사자 모두의 것이라 조건 없이 선다(DOC-87 §3-7).
                  인쇄가 아니라 내려받기다.
                */}
                <button
                  type="button"
                  className="btn-secondary px-3 py-1.5 text-ui-xs"
                  onClick={() => report.mutate({ id: row.id, fileName: reportFileName(row) })}
                  disabled={report.isPending}
                >
                  <MenuIcon name="download" size={13} />
                  <span className="ml-1">보고서</span>
                </button>
              </div>
            }
          />

          {report.isError && (
            <p className="mb-3 text-ui-xs" style={{ color: 'var(--danger)' }}>보고서를 내려받지 못했습니다.</p>
          )}

          <dl className="card mb-3 flex flex-col gap-1 text-ui-xs">
            <div className="flex justify-between gap-3">
              <dt style={{ color: 'var(--text-secondary)' }}>검측일</dt>
              <dd>{shortDate(row.inspectionDate)}</dd>
            </div>
            {row.location !== null && (
              <div className="flex justify-between gap-3">
                <dt style={{ color: 'var(--text-secondary)' }}>위치</dt>
                <dd className="text-right">{row.location}</dd>
              </div>
            )}
            <div className="flex justify-between gap-3">
              <dt style={{ color: 'var(--text-secondary)' }}>요청</dt>
              <dd>{row.requester ?? '-'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt style={{ color: 'var(--text-secondary)' }}>검측자</dt>
              <dd>{row.inspector ?? '-'}</dd>
            </div>
          </dl>

          {/*
            사진 — 증빙을 만드는 손이 요청 상태에서만 붙인다(DOC-84 §3-D-3 · DOC-87 §3-4). 판정이 난 검측에
            증빙을 더하면 무엇을 보고 판정했는지가 흐려진다. 감리는 본다. 사진이 없고 붙일 수도 없으면 칸을 세우지 않는다.
          */}
          {(row.photoCount > 0 || (canSubmitEvidence && row.status === 'REQUESTED')) && (
            <div className="card mb-3">
              <div className="mb-2 text-ui-xs font-medium">사진{row.photoCount > 0 && ` ${row.photoCount}장`}</div>
              <PhotoGrid
                target="inspections"
                parentId={row.id}
                canUpload={canSubmitEvidence && row.status === 'REQUESTED'}
                onUploaded={() => {
                  void queryClient.invalidateQueries({ queryKey: keys.inspection(row.id) });
                  void queryClient.invalidateQueries({ queryKey: keys.inspections(siteId) });
                }}
              />
            </div>
          )}

          {row.resultNote !== null && (
            <div className="card mb-3">
              <div className="mb-1 flex items-center gap-2">
                <span className="text-ui-xs font-medium">지적사항</span>
                {row.defectType !== null && <span className="badge badge-red">{row.defectType}</span>}
              </div>
              <p className="text-ui-sm">{row.resultNote}</p>
            </div>
          )}

          {/* ── 판정하는 손: 착수·판정 ─────────────────────────────────────── */}
          {isJudge && row.status === 'REQUESTED' && (
            <button type="button" className="btn-primary w-full" onClick={() => start.mutate()} disabled={start.isPending}>
              검측 착수
            </button>
          )}

          {isJudge && row.status === 'IN_REVIEW' && !verdictOpen && (
            <button type="button" className="btn-primary w-full" onClick={() => setVerdictOpen(true)}>
              판정
            </button>
          )}

          {isJudge && verdictOpen && (
            <div className="card">
              <label className="mb-3 block">
                <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>결함 유형</span>
                <select className="input-field" value={defectType} onChange={(e) => setDefectType(e.target.value as DefectType)}>
                  {DEFECT_TYPES.map((it) => <option key={it} value={it}>{it}</option>)}
                </select>
              </label>
              <label className="mb-4 block">
                <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
                  지적사항 <span style={{ color: 'var(--danger)' }}>*</span>
                </span>
                <textarea
                  className="input-field min-h-24"
                  value={resultNote}
                  onChange={(e) => setResultNote(e.target.value)}
                  rows={4}
                />
              </label>
              <div className="flex gap-2">
                <button type="button" className="btn-primary flex-1" onClick={() => onJudge('PASSED')} disabled={judge.isPending}>
                  합격
                </button>
                <button
                  type="button"
                  className="btn-secondary flex-1"
                  onClick={() => onJudge('FAILED')}
                  disabled={judge.isPending || resultNote.trim() === ''}
                  style={{ color: 'var(--danger)' }}
                >
                  불합격
                </button>
              </div>
            </div>
          )}

          {/* ── 증빙을 만드는 손: 재검 요청 ─────────────────────────────────── */}
          {canSubmitEvidence && row.status === 'FAILED' && (
            <button type="button" className="btn-primary w-full" onClick={onRecheck} disabled={recheck.isPending}>
              {recheck.isPending ? '보내는 중입니다…' : '재검 요청'}
            </button>
          )}
        </>
      )}
    </QueryState>
  );
}

/** 내려받는 파일 이름. 서버가 RFC 5987 로 붙여 주지만, blob 으로 받으면 그 이름이 오지 않아 화면이 짓는다. */
function reportFileName(row: Inspection): string {
  const date = row.inspectionDate ?? '';
  const round = row.roundNo > 1 ? `_${row.roundNo}차` : '';
  return `검측보고서_${row.title}${round}_${date}.pdf`.replace(/[\\/:*?"<>|]/g, '_');
}
